<script setup lang="ts">
import { useCoreStore } from '@apps/main/new-desktop/core/stores';

import type { ContentApiImage } from '@libs/trip-planner-interfaces';
import { AppButton, AppMasonryGallery, AppSwiperGallery } from '@libs/ui';

import { GalleryType } from '../models';
import { useHotelDetailsGalleryStore } from '../stores';

interface HotelDetailsGallerySectionProps {
  galleryType: GalleryType;
  hotelImages: ContentApiImage[];
}
const props = defineProps<HotelDetailsGallerySectionProps>();

interface HotelDetailsGallerySectionEmits {
  (event: 'close'): void;
  (eventName: 'show-full-gallery'): void;
  (eventName: 'show-single-image', imageIndex: number): void;
}
const emit = defineEmits<HotelDetailsGallerySectionEmits>();

const hotelDetailsGalleryStore = useHotelDetailsGalleryStore();
const coreStore = useCoreStore();
</script>

<template>
  <div class="hotel-details-gallery-section">
    <AppSwiperGallery
      v-if="props.galleryType === GalleryType.Swiper"
      :image-urls="
        hotelDetailsGalleryStore.getImagesForSwipperGallery(props.hotelImages)
      "
      :display-gallery-dots="false"
      :error-image-handler="coreStore.handleImageLoadError"
      @show-full-gallery="emit('show-full-gallery')"
      @show-single-image="emit('show-single-image', $event)"
    />
    <AppMasonryGallery
      v-if="
        props.galleryType === GalleryType.Masonry && props.hotelImages.length
      "
      :full-gallery-button-text="
        $t('planner.show_all_x_images', [props.hotelImages.length])
      "
      :image-urls="
        hotelDetailsGalleryStore.getImagesForMasonryGallery(props.hotelImages)
      "
      :alt-image-text="$t('a11y.image')"
      class="masonry-gallery"
      @show-full-gallery="emit('show-full-gallery')"
    />

    <div class="floating-buttons">
      <AppButton
        type="secondary"
        state="enabled"
        size="medium"
        :aria-label="$t('a11y.close_panel')"
        :icon="{ name: 'close' }"
        @click="emit('close')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-details-gallery-section {
  background-color: var(--color-white);
  position: relative;

  @include for-more-or-equal-than-1200-px {
    border-radius: var(--border-radius-12);
  }

  .masonry-gallery {
    width: 100%;
    height: 425px;
  }

  .floating-buttons {
    position: absolute;
    top: var(--spacing-16);
    right: var(--spacing-16);
  }
}
</style>
